import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trash2, X } from 'lucide-react';
import { api } from '../api/client';
import { fmtCHF, fmtCHF2, fmtMoisFr } from '../utils/format';
import { FluxDetail, LigneFlux, ScenarioBudget, MOIS_LABELS } from '../types';
import { BadgeTagRevenu, ChoixTagScenario } from './TagScenarioRevenu';
import { HistoriqueRevenu } from './HistoriqueRevenu';

type Onglet = 'valeurs' | 'recettes' | 'contrat' | 'infos';

interface Props {
  fluxId: number;
  annee: number;
  moisInitial: number; // 1..12
  ligne: LigneFlux | null; // ligne de grille (valeurs en vigueur par mois)
  scenarios: ScenarioBudget[];
  scenarioCourant: number | null;
  onFermer: () => void;
  onModifie: () => void;
}

/**
 * Fiche complète d'un flux de revenu : historique des événements, recettes
 * libres, référence au mandant et au contrat (lecture seule — l'édition vit
 * dans les modules Clients / Contrats), paramètres du flux.
 */
export function FluxModal({ fluxId, annee, moisInitial, ligne, scenarios, scenarioCourant, onFermer, onModifie }: Props) {
  const [detail, setDetail] = useState<FluxDetail | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(() => {
    api.get<FluxDetail>(`/revenus/flux/${fluxId}`)
      .then(setDetail)
      .catch((e) => setErreur(e.message));
  }, [fluxId]);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFermer]);

  const mode = detail?.flux.mode ?? ligne?.mode ?? 'montant';
  const reference = detail?.reference ?? null;
  const ongletInitial: Onglet = mode === 'libre' ? 'recettes' : mode === 'convention' ? 'contrat' : 'valeurs';
  const [onglet, setOnglet] = useState<Onglet>(ongletInitial);
  useEffect(() => { setOnglet(ongletInitial); }, [ongletInitial]);

  const nomScenario = useCallback((id: number | null) => {
    if (id == null) return null;
    return scenarios.find((s) => s.id === id)?.nom ?? `#${id}`;
  }, [scenarios]);

  async function supprimerEvenement(chemin: string, id: number) {
    const c = window.prompt('Commentaire de suppression (obligatoire) :');
    if (!c || !c.trim()) return;
    setErreur(null);
    try {
      await api.delete(`/revenus/${chemin}/${id}`, { commentaire: c.trim() });
      charger();
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
    }
  }

  // ── Formulaire « modifier dès… » (modes montant / quantite_tarif) ────────
  const [moisEffet, setMoisEffet] = useState(`${annee}-${String(moisInitial).padStart(2, '0')}`);
  // `ligne` ne porte que l'année affichée dans la grille : un mois d'effet d'un
  // autre millésime n'a donc aucune valeur de référence ici. On ne préremplit
  // ni ne compare dans ce cas — sinon on opposerait la saisie au même mois de
  // l'AUTRE année (bouton grisé à tort, aperçu et écart faux).
  const horsAnnee = moisEffet.slice(0, 4) !== String(annee);
  const indexMois = Math.max(0, Math.min(11, Number(moisEffet.slice(5, 7)) - 1));
  const celluleRef = horsAnnee ? undefined : ligne?.mois[indexMois];
  const [montant, setMontant] = useState('');
  const [quantite, setQuantite] = useState('');
  const [tag, setTag] = useState<'definitif' | 'projete'>(scenarioCourant != null ? 'projete' : 'definitif');
  const [scenarioId, setScenarioId] = useState<number | null>(scenarioCourant);
  const [commentaire, setCommentaire] = useState('');

  useEffect(() => {
    // Préremplit avec les valeurs en vigueur au mois choisi (année affichée
    // seulement) ; hors de cette année, on repart de champs vides.
    if (celluleRef) {
      setMontant(String(Math.round(celluleRef.montant * 100) / 100));
      setQuantite(String(celluleRef.quantite ?? 0));
    } else {
      setMontant('');
      setQuantite('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moisEffet, fluxId]);

  const estQT = mode === 'quantite_tarif';
  // Prestation facturée au temps passé (coaching OAI, art. 15) : le montant se
  // saisit, la quantité d'assurés reste un indicateur — voir services/revenus.
  // Propriété du flux (unité de la prestation), pas du mois : on la lit sur la
  // ligne entière pour qu'elle tienne aussi hors de l'année affichée.
  const estHoraire = estQT && !!ligne?.mois.some((c) => c.tarifHoraire);
  const tarifConnu = celluleRef != null;
  const tarifEnVigueur = celluleRef?.tarif ?? 0;
  const montantNum = Number(montant);
  const quantiteNum = Number(quantite);
  // Sans référence connue, toute valeur effectivement saisie fait changement ;
  // un champ laissé vide n'enregistre rien (il ne vaut pas « 0 »).
  const montantChange = (!estQT || estHoraire) && montant.trim() !== ''
    && (celluleRef ? Math.abs(montantNum - celluleRef.montant) > 0.004 : true);
  const quantiteChange = estQT && quantite.trim() !== ''
    && (celluleRef ? Math.abs(quantiteNum - (celluleRef.quantite ?? 0)) > 0.004 : true);
  const formulaireValide =
    /^\d{4}-\d{2}$/.test(moisEffet) &&
    montantNum >= 0 && quantiteNum >= 0 &&
    commentaire.trim().length > 0 &&
    (tag === 'definitif' || scenarioId != null) &&
    (montantChange || quantiteChange);

  async function enregistrerChangement() {
    if (!formulaireValide || enCours) return;
    setEnCours(true);
    setErreur(null);
    const base = tag === 'projete' ? { tag, scenarioId } : { tag };
    try {
      if (montantChange) {
        await api.post(`/revenus/flux/${fluxId}/montant-evenements`, {
          moisEffet, montant: montantNum, ...base, commentaire: commentaire.trim(),
        });
      }
      if (quantiteChange) {
        await api.post(`/revenus/flux/${fluxId}/quantite-evenements`, {
          moisEffet, quantite: quantiteNum, ...base, commentaire: commentaire.trim(),
        });
      }
      setCommentaire('');
      charger();
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
    } finally {
      setEnCours(false);
    }
  }

  // ── Recettes libres ───────────────────────────────────────────────────────
  const [recMois, setRecMois] = useState(`${annee}-${String(moisInitial).padStart(2, '0')}`);
  const [recLibelle, setRecLibelle] = useState('');
  const [recMontant, setRecMontant] = useState('');
  const [recTag, setRecTag] = useState<'definitif' | 'projete'>(scenarioCourant != null ? 'projete' : 'definitif');
  const [recScenarioId, setRecScenarioId] = useState<number | null>(scenarioCourant);
  const [recCommentaire, setRecCommentaire] = useState('');
  const recetteValide =
    /^\d{4}-\d{2}$/.test(recMois) &&
    recLibelle.trim().length > 0 &&
    Number.isFinite(Number(recMontant)) && recMontant.trim() !== '' &&
    recCommentaire.trim().length > 0 &&
    (recTag === 'definitif' || recScenarioId != null);

  async function ajouterRecette() {
    if (!recetteValide || enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      await api.post(`/revenus/flux/${fluxId}/recettes`, {
        mois: recMois, libelle: recLibelle.trim(), montant: Number(recMontant),
        ...(recTag === 'projete' ? { tag: recTag, scenarioId: recScenarioId } : { tag: recTag }),
        commentaire: recCommentaire.trim(),
      });
      setRecLibelle('');
      setRecMontant('');
      setRecCommentaire('');
      charger();
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
    } finally {
      setEnCours(false);
    }
  }

  // ── Infos (paramètres du flux) ────────────────────────────────────────────
  const [infoLibelle, setInfoLibelle] = useState('');
  const [infoMoisFact, setInfoMoisFact] = useState(''); // '1,4,7,10' ; vide = tous
  const [infoLag, setInfoLag] = useState('0');
  const [infoNote, setInfoNote] = useState('');
  const [infoActif, setInfoActif] = useState(true);
  const [infoCommentaire, setInfoCommentaire] = useState('');

  useEffect(() => {
    if (detail) {
      setInfoLibelle(detail.flux.libelle);
      setInfoMoisFact(detail.flux.mois_facturation?.join(',') ?? '');
      setInfoLag(String(detail.flux.lag_encaissement));
      setInfoNote(detail.flux.note ?? '');
      setInfoActif(detail.flux.actif);
    }
  }, [detail]);

  const moisFactValides = infoMoisFact.trim() === '' ||
    infoMoisFact.split(',').every((v) => { const n = Number(v.trim()); return n >= 1 && n <= 12; });

  async function enregistrerInfos() {
    if (!infoCommentaire.trim() || !moisFactValides || enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      await api.put(`/revenus/flux/${fluxId}`, {
        libelle: infoLibelle,
        moisFacturation: infoMoisFact.trim() === '' ? null : infoMoisFact.split(',').map((v) => Number(v.trim())),
        lagEncaissement: Number(infoLag) || 0,
        actif: infoActif,
        note: infoNote || null,
        commentaire: infoCommentaire.trim(),
      });
      setInfoCommentaire('');
      charger();
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
    } finally {
      setEnCours(false);
    }
  }

  const apercu = estQT && !estHoraire ? quantiteNum * tarifEnVigueur : montantNum;
  const recettesTriees = useMemo(
    () => (detail?.recettes ?? []).slice().sort((a, b) => b.mois.localeCompare(a.mois)),
    [detail]
  );

  const termeAnnee = reference?.contrat?.termes.find((t) => t.annee === annee) ?? null;

  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal modal-wide" style={{ maxWidth: 760, height: 'min(85vh, 820px)' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{detail?.flux.libelle ?? '…'}</div>
            <div className="text-xs text-ink-3">
              {detail?.flux.groupe}
              {reference?.client ? ` · ${reference.client.chemin}` : ''}
              {detail?.flux.programme
                ? ` · programme ${detail.flux.programme}${detail.flux.contrat_id ? ' (conventionné)' : ' (individualisé)'}`
                : ''}
              {detail?.flux.mois_facturation
                ? ` · facturation mois ${detail.flux.mois_facturation.join(', ')}`
                : ' · facturation mensuelle'}
              {detail && detail.flux.lag_encaissement > 0
                ? ` · encaissé ${detail.flux.lag_encaissement} mois plus tard` : ''}
            </div>
          </div>
          <button className="close-btn" onClick={onFermer} title="Fermer (Esc)"><X className="icon-sm" /></button>
        </div>

        <div className="modal-body" style={{ overflowY: 'auto' }}>
          <div className="tabs mb-16">
            {mode !== 'libre' && mode !== 'convention' && (
              <button className={`tab${onglet === 'valeurs' ? ' active' : ''}`} onClick={() => setOnglet('valeurs')}>
                {estQT ? 'Quantités & tarifs' : 'Montants'}
              </button>
            )}
            {mode === 'libre' && (
              <button className={`tab${onglet === 'recettes' ? ' active' : ''}`} onClick={() => setOnglet('recettes')}>Recettes</button>
            )}
            {(reference?.client || reference?.contrat || reference?.prestation) && (
              <button className={`tab${onglet === 'contrat' ? ' active' : ''}`} onClick={() => setOnglet('contrat')}>
                Mandant & contrat
              </button>
            )}
            <button className={`tab${onglet === 'infos' ? ' active' : ''}`} onClick={() => setOnglet('infos')}>Paramètres</button>
          </div>

          {erreur && <div className="text-err mb-16">{erreur}</div>}

          {onglet === 'valeurs' && (
            <>
              <div className="formulaire-changement mb-16">
                <div className="formulaire-changement__titre">Modifier à partir d'un mois donné</div>
                <div className="flex gap-12 flex-wrap items-end">
                  <div className="form-group">
                    <label className="form-label">À partir de</label>
                    <input type="month" className="form-input" value={moisEffet} onChange={(e) => setMoisEffet(e.target.value)} />
                  </div>
                  {estQT ? (
                    <>
                      <div className="form-group">
                        <label className="form-label">Quantité / mois</label>
                        <input type="number" className="form-input" style={{ width: 100 }} min={0} step={1}
                          value={quantite} onChange={(e) => setQuantite(e.target.value)} />
                      </div>
                      {estHoraire && (
                        <div className="form-group">
                          <label className="form-label">Montant (CHF / mois)</label>
                          <input type="number" className="form-input" style={{ width: 140 }} min={0} step={0.05}
                            value={montant} onChange={(e) => setMontant(e.target.value)} />
                        </div>
                      )}
                      <div className="form-group">
                        <label className="form-label">Tarif en vigueur</label>
                        <div className="text-sm" style={{ paddingTop: 6 }}>
                          <strong>{tarifConnu
                            ? `CHF ${fmtCHF2(tarifEnVigueur)}${estHoraire ? ' /h' : ''}`
                            : '—'}</strong>{' '}
                          <a href="#contrats" className="text-accent" title="Le tarif fait foi dans la convention cadre — modifier dans Contrats"
                            onClick={onFermer}>
                            convention cadre ↗
                          </a>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="form-group">
                      <label className="form-label">Montant (CHF{detail?.flux.mois_facturation ? ' / versement' : ' / mois'})</label>
                      <input type="number" className="form-input" style={{ width: 140 }} min={0} step={0.05}
                        value={montant} onChange={(e) => setMontant(e.target.value)} />
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">Statut</label>
                    <ChoixTagScenario tag={tag} scenarioId={scenarioId} scenarios={scenarios}
                      onChange={(t, s) => { setTag(t); setScenarioId(s); }} />
                  </div>
                </div>
                <div className="form-group mt-8">
                  <label className="form-label">Commentaire (obligatoire)</label>
                  <input className="form-input w-full" placeholder="Motif de la décision ou de l'hypothèse…"
                    value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
                </div>
                <div className="flex items-center justify-between mt-8">
                  <span className="text-sm text-ink-2">
                    {horsAnnee ? (
                      <>
                        Dès {fmtMoisFr(`${moisEffet}-01`)} — <strong>hors de l'année affichée ({annee})</strong> :
                        aucune valeur de référence, saisir directement la valeur voulue
                        {estQT && !estHoraire ? ' (le tarif du mois visé sera appliqué à la quantité)' : ''}.
                      </>
                    ) : (
                      <>
                        Dès {fmtMoisFr(`${moisEffet}-01`)} : <strong>CHF {fmtCHF2(apercu)}</strong>
                        {estQT && (estHoraire
                          ? ` (${quantiteNum} assurés · tarif ${fmtCHF2(tarifEnVigueur)}/h : le montant ne se dérive pas de la quantité)`
                          : ` (${quantiteNum} × ${fmtCHF2(tarifEnVigueur)})`)}
                      </>
                    )}
                  </span>
                  <button className="btn btn-accent" disabled={!formulaireValide || enCours} onClick={enregistrerChangement}>
                    {enCours ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                </div>
              </div>

              {estQT ? (
                <>
                  <div className="flex gap-16" style={{ alignItems: 'flex-start' }}>
                    <HistoriqueRevenu
                      titre="Quantités (assurés)"
                      lignes={(detail?.quantiteEvenements ?? []).map((e) => ({
                        id: e.id, mois: e.mois_effet, valeur: String(Number(e.quantite)),
                        tag: e.tag, scenarioNom: nomScenario(e.scenario_id), commentaire: e.commentaire,
                      }))}
                      onSupprimer={(id) => supprimerEvenement('quantite-evenements', id)}
                    />
                    <HistoriqueRevenu
                      titre={`Tarifs ${estHoraire ? '(convention cadre, à l’heure' : '(convention cadre'} — lecture seule)`}
                      lignes={(reference?.prestation?.tarifs ?? []).map((e) => ({
                        id: e.id, mois: e.mois_effet,
                        valeur: `CHF ${fmtCHF2(Number(e.tarif))}${estHoraire ? ' / heure' : ''}`,
                        tag: e.tag, scenarioNom: nomScenario(e.scenario_id), commentaire: e.commentaire,
                      }))}
                    />
                  </div>
                  {(detail?.montantEvenements.length ?? 0) > 0 && (
                    <div className="mt-16">
                      <HistoriqueRevenu
                        titre={estHoraire
                          ? 'Montants facturés / budgétés (tarif horaire : le montant fait foi)'
                          : 'Montants facturés réels (remplacent quantité × tarif pour leur mois)'}
                        lignes={(detail?.montantEvenements ?? []).map((e) => ({
                          id: e.id, mois: e.mois_effet, valeur: `CHF ${fmtCHF2(Number(e.montant))}`,
                          tag: e.tag, scenarioNom: nomScenario(e.scenario_id), commentaire: e.commentaire,
                        }))}
                        onSupprimer={(id) => supprimerEvenement('montant-evenements', id)}
                      />
                    </div>
                  )}
                </>
              ) : (
                <HistoriqueRevenu
                  titre="Montants"
                  lignes={(detail?.montantEvenements ?? []).map((e) => ({
                    id: e.id, mois: e.mois_effet, valeur: `CHF ${fmtCHF2(Number(e.montant))}`,
                    tag: e.tag, scenarioNom: nomScenario(e.scenario_id), commentaire: e.commentaire,
                  }))}
                  onSupprimer={(id) => supprimerEvenement('montant-evenements', id)}
                />
              )}
            </>
          )}

          {onglet === 'recettes' && (
            <>
              <div className="formulaire-changement mb-16">
                <div className="formulaire-changement__titre">Nouvelle recette (ponctuelle, sans report)</div>
                <div className="flex gap-12 flex-wrap items-end">
                  <div className="form-group">
                    <label className="form-label">Mois</label>
                    <input type="month" className="form-input" value={recMois} onChange={(e) => setRecMois(e.target.value)} />
                  </div>
                  <div className="form-group" style={{ flex: 1, minWidth: 180 }}>
                    <label className="form-label">Libellé</label>
                    <input className="form-input w-full" placeholder="Ex. contribution SSL, don…"
                      value={recLibelle} onChange={(e) => setRecLibelle(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Montant (CHF)</label>
                    <input type="number" className="form-input" style={{ width: 120 }} step={0.05}
                      value={recMontant} onChange={(e) => setRecMontant(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Statut</label>
                    <ChoixTagScenario tag={recTag} scenarioId={recScenarioId} scenarios={scenarios}
                      onChange={(t, s) => { setRecTag(t); setRecScenarioId(s); }} />
                  </div>
                </div>
                <div className="form-group mt-8">
                  <label className="form-label">Commentaire (obligatoire)</label>
                  <input className="form-input w-full" placeholder="Provenance, référence…"
                    value={recCommentaire} onChange={(e) => setRecCommentaire(e.target.value)} />
                </div>
                <div className="flex justify-end mt-8">
                  <button className="btn btn-accent" disabled={!recetteValide || enCours} onClick={ajouterRecette}>
                    {enCours ? 'Enregistrement…' : 'Ajouter'}
                  </button>
                </div>
              </div>

              <h3 className="text-base font-semi mb-8">Recettes enregistrées</h3>
              <table className="table table-compact">
                <thead>
                  <tr><th>Mois</th><th>Libellé</th><th className="text-right">Montant</th><th>Statut</th><th style={{ width: 32 }} /></tr>
                </thead>
                <tbody>
                  {recettesTriees.map((r) => (
                    <tr key={r.id} title={r.commentaire}>
                      <td className="tabular-nums">{fmtMoisFr(r.mois)}</td>
                      <td>{r.libelle}</td>
                      <td className="text-right tabular-nums">CHF {fmtCHF2(Number(r.montant))}</td>
                      <td><BadgeTagRevenu tag={r.tag} scenarioNom={nomScenario(r.scenario_id)} /></td>
                      <td>
                        <button className="btn btn-icon" title="Supprimer" onClick={() => supprimerEvenement('recettes', r.id)}>
                          <Trash2 className="icon-sm" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {recettesTriees.length === 0 && <tr><td colSpan={5} className="text-ink-3">Aucune recette.</td></tr>}
                </tbody>
              </table>
            </>
          )}

          {onglet === 'contrat' && reference && (
            <div className="flex flex-col gap-16">
              {mode === 'convention' && (
                <div className="formulaire-changement" style={{ marginBottom: 0 }}>
                  <div className="formulaire-changement__titre">Montant dérivé de la convention</div>
                  <p className="text-sm text-ink-2" style={{ margin: 0 }}>
                    Ce flux est calculé automatiquement à partir du terme annuel : un mois déjà décompté vaut
                    {' '}<strong>mois-places × prix mensuel par place</strong> (décompte du module{' '}
                    <a href="#mesures" className="text-accent" onClick={onFermer} title="Ouvrir le module Mesures">Mesures ↗</a>),
                    {' '}et le <strong>solde du plafond</strong> se répartit sur les mois non encore facturés. Le total de
                    l'année égale donc toujours le plafond ; une fois celui-ci atteint, les mois suivants valent 0.
                    {termeAnnee
                      ? <> Pour {annee} : {Number(termeAnnee.places)} places × CHF {fmtCHF2(Number(termeAnnee.prix) / 12)} /pl./mois
                          {' '}(CHF {fmtCHF2(Number(termeAnnee.prix))} /pl./an) = plafond{' '}
                          <strong>CHF {fmtCHF(Number(termeAnnee.prix) * Number(termeAnnee.places))}</strong> / an.</>
                      : <> Aucun terme saisi pour {annee} — le flux vaut 0 tant que la convention n'est pas enregistrée dans Contrats.</>}
                  </p>
                </div>
              )}

              {reference.client && (
                <div>
                  <h3 className="text-base font-semi mb-8">Mandant</h3>
                  <p className="text-sm" style={{ margin: 0 }}>
                    {reference.client.chemin}
                    {' '}<a href="#clients" className="text-accent" onClick={onFermer} title="Ouvrir le référentiel Clients">fiche ↗</a>
                  </p>
                </div>
              )}

              {reference.contrat && (
                <div>
                  <h3 className="text-base font-semi mb-8">
                    {reference.contrat.libelle}
                    {' '}<a href="#contrats" className="text-accent" onClick={onFermer} title="Modifier dans Contrats">ouvrir dans Contrats ↗</a>
                  </h3>
                  <table className="table table-compact">
                    <thead>
                      <tr><th>Année</th><th className="text-right">Prix / pl. / mois</th><th className="text-right">Prix / pl. / an</th>
                        <th className="text-right">Places</th><th className="text-right">Plafond / an</th><th>Statut</th></tr>
                    </thead>
                    <tbody>
                      {reference.contrat.termes.map((t) => (
                        <tr key={t.id} title={t.commentaire}>
                          <td className="tabular-nums">{t.annee}</td>
                          <td className="text-right tabular-nums">CHF {fmtCHF2(Number(t.prix) / 12)}</td>
                          <td className="text-right tabular-nums">CHF {fmtCHF2(Number(t.prix))}</td>
                          <td className="text-right tabular-nums">{Number(t.places)}</td>
                          <td className="text-right tabular-nums">CHF {fmtCHF(Number(t.prix) * Number(t.places))}</td>
                          <td><BadgeTagRevenu tag={t.tag} scenarioNom={nomScenario(t.scenario_id)} /></td>
                        </tr>
                      ))}
                      {reference.contrat.termes.length === 0 && <tr><td colSpan={6} className="text-ink-3">Aucun terme saisi.</td></tr>}
                    </tbody>
                  </table>
                  {mode === 'convention' && (
                    <p className="text-xs text-ink-3">
                      Le décompte des places utilisées (rétrocession, sur-utilisation) se saisit dans le module{' '}
                      <a href="#mesures" className="text-accent" onClick={onFermer}>Mesures ↗</a>.
                    </p>
                  )}
                </div>
              )}

              {reference.prestation && (
                <div>
                  <h3 className="text-base font-semi mb-8">
                    Tarifs — {reference.prestation.libelle} ({reference.prestation.contrat_libelle})
                    {' '}<a href="#contrats" className="text-accent" onClick={onFermer} title="Modifier dans Contrats">ouvrir dans Contrats ↗</a>
                  </h3>
                  <HistoriqueRevenu
                    titre=""
                    lignes={reference.prestation.tarifs.map((t) => ({
                      id: t.id, mois: t.mois_effet,
                      valeur: `CHF ${fmtCHF2(Number(t.tarif))}${estHoraire ? ' / heure' : ''}`,
                      tag: t.tag, scenarioNom: nomScenario(t.scenario_id), commentaire: t.commentaire,
                    }))}
                  />
                </div>
              )}
            </div>
          )}

          {onglet === 'infos' && detail && (
            <div className="flex flex-col gap-12">
              <div className="form-group">
                <label className="form-label">Libellé</label>
                <input className="form-input w-full" value={infoLibelle} onChange={(e) => setInfoLibelle(e.target.value)} />
              </div>
              <div className="flex gap-12 flex-wrap">
                <div className="form-group">
                  <label className="form-label">Mois de facturation <span className="text-ink-3">(ex. 1,4,7,10 — vide = tous)</span></label>
                  <input className="form-input" style={{ width: 160 }} placeholder="tous"
                    value={infoMoisFact} onChange={(e) => setInfoMoisFact(e.target.value)} />
                  {!moisFactValides && <div className="text-err text-xs mt-8">Mois invalides (1–12, séparés par des virgules).</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Encaissement <span className="text-ink-3">(mois après facturation)</span></label>
                  <input type="number" className="form-input" style={{ width: 90 }} min={0} max={6} step={1}
                    value={infoLag} onChange={(e) => setInfoLag(e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Note</label>
                <input className="form-input w-full" value={infoNote} onChange={(e) => setInfoNote(e.target.value)} />
              </div>
              <label className="toggle-label">
                <span className="toggle">
                  <input type="checkbox" className="toggle-input" checked={infoActif} onChange={(e) => setInfoActif(e.target.checked)} />
                  <span className="toggle-slider" />
                </span>
                <span className="text-sm">Flux actif <span className="text-ink-3">(un flux inactif disparaît de la grille — pour arrêter un flux à une date donnée, préférer un montant 0 dès ce mois)</span></span>
              </label>
              <div className="form-group">
                <label className="form-label">Commentaire de modification (obligatoire)</label>
                <input className="form-input w-full" value={infoCommentaire} onChange={(e) => setInfoCommentaire(e.target.value)} />
              </div>
              <div className="flex justify-end">
                <button className="btn btn-accent" disabled={!infoCommentaire.trim() || !moisFactValides || enCours} onClick={enregistrerInfos}>
                  {enCours ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
